import Icon from '@components/v2/ui/Icon/Icon';
import { TextFieldProps } from '@mui/material';
import {
  DateValidationError,
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
  PickerChangeHandlerContext,
  PickersActionBarAction,
} from '@mui/x-date-pickers';
import { Calendar } from 'lucide-react';
import { forwardRef, useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';

const OpenPickerIcon = () => <Icon icon={Calendar} size="md" />;

export interface DatePickerProps
  extends Pick<
      MuiDatePickerProps<Date>,
      'label' | 'disabled' | 'disablePast' | 'minDate' | 'maxDate'
    >,
    Pick<
      TextFieldProps,
      'error' | 'helperText' | 'size' | 'name' | 'required' | 'fullWidth' | 'onBlur'
    > {
  value: Date | null;
  onChange: (date: Date | null) => void;
  onValidationErrorChange?: (error: DateValidationError) => void;
  clearable?: boolean;
  testId?: string;
}

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      onValidationErrorChange,
      label,
      error,
      helperText,
      disabled,
      size = 'medium',
      name,
      required,
      fullWidth = true,
      clearable = true,
      disablePast,
      minDate,
      maxDate,
      testId,
      onBlur,
    },
    ref,
  ) => {
    const { translate } = useTranslation();
    const [validationError, setValidationError] = useState<DateValidationError>(null);

    const actions: PickersActionBarAction[] = clearable ? ['clear', 'accept'] : ['accept'];
    const hasError = error ?? Boolean(validationError);
    const resolvedHelperText =
      helperText ?? (validationError ? translate(LangEnum.GLOBAL_INVALID_DATE) : undefined);

    const handleChange = (
      date: Date | null,
      context: PickerChangeHandlerContext<DateValidationError>,
    ) => {
      setValidationError(context.validationError);
      onValidationErrorChange?.(context.validationError);

      onChange(date);
    };

    return (
      <MuiDatePicker
        value={value}
        label={label}
        inputRef={ref}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        disablePast={disablePast}
        onChange={handleChange}
        slots={{ openPickerIcon: OpenPickerIcon }}
        slotProps={{
          actionBar: { actions },
          openPickerButton: { sx: { marginRight: 0 } },
          field: {
            clearable,
            onClear: () => {
              setValidationError(null);
              onValidationErrorChange?.(null);
              onChange(null);
            },
          },
          textField: {
            name,
            size,
            required,
            fullWidth,
            onBlur,
            error: hasError,
            helperText: resolvedHelperText,
            slotProps: { htmlInput: { 'test-id': testId } },
          },
        }}
      />
    );
  },
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
